import {
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import clsx from 'clsx';
import {
  type FocusEvent,
  Fragment,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import useMe from '@/renderer/utils/useMe';
import type { PressEvent } from 'react-aria';
import { twMerge } from 'tailwind-merge';
import { AccessibleIcon } from '../react-aria/accessible-icon';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItemLabel,
  MenuSeparator,
  MenuTrigger,
} from '../react-aria/menu';
import { Popover } from '../react-aria/popover';

type Link = {
  href?: string;
  text?: string;
  icon?: React.ForwardRefExoticComponent<
    React.PropsWithoutRef<React.SVGProps<SVGSVGElement>> & {
      title?: string;
      titleId?: string;
    } & React.RefAttributes<SVGSVGElement>
  >;
  divider?: boolean;
  destructive?: true;
  className?: string;
};

const LINKS: Link[] = [
  {
    href: '/privacy',
    text: 'Privacy Policy',
  },
  {
    href: '/terms',
    text: 'Terms & Conditions',
  },
  {
    divider: true,
  },
  {
    href: '/settings',
    text: 'Account Settings',
    icon: Cog6ToothIcon,
  },
  {
    href: '/signout',
    text: 'Sign Out',
    icon: ArrowRightStartOnRectangleIcon,
    destructive: true,
  },
  {
    divider: true,
    className: '[body.page-is-interactive_&]:hidden',
  },
  {
    text: 'Close',
    icon: XMarkIcon,
    className: '[body.page-is-interactive_&]:hidden',
  },
];

export default function UserDropdown(props: {
  user?: API.User | null | undefined;
  withoutUser?: boolean;
}) {
  // biome-ignore lint/correctness/useHookAtTopLevel: This is used in the <Suspense> of Nav, so it never changes
  const me = props?.withoutUser ? null : useMe();
  const user = props?.withoutUser ? me || props?.user : props?.user || me;

  const [isLoading, setIsLoading] = useState(user === undefined);
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<number | NodeJS.Timeout>(0);

  useEffect(() => {
    setIsReady(true);

    return () => clearTimeout(timeoutRef.current);
  }, []);

  const onMenuButtonEvent = useCallback((_event: FocusEvent | KeyboardEvent | PressEvent) => {
    const focusedElement = (document.querySelector('#user-nav a:focus') ||
      document.activeElement) as HTMLAnchorElement;

    if (focusedElement?.dataset?.usernav) {
      focusedElement.blur();
      clearTimeout(timeoutRef.current);
      setIsOpen(false);
    } else {
      (document.querySelector('#user-nav a') as HTMLAnchorElement)?.focus();
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setIsOpen(true), 200);
    }
  }, []);

  const onClose = useCallback(() => {
    const focusedElement = (document.querySelector('#user-nav a:focus') ||
      document.activeElement) as HTMLAnchorElement;

    if (focusedElement?.dataset?.usernav) {
      focusedElement.blur();
    }

    clearTimeout(timeoutRef.current);
    setIsOpen(false);
  }, []);

  const onBlur = useCallback((event: FocusEvent<HTMLDivElement, Element>) => {
    // const from = event.target;
    const to = event.relatedTarget as HTMLAnchorElement;

    const focusedElement = (document.querySelector('#user-nav a:focus') ||
      document.activeElement) as HTMLAnchorElement;

    if (!focusedElement?.dataset?.usernav && !to?.dataset?.usernav) {
      clearTimeout(timeoutRef.current);
      setIsOpen(false);
    }
  }, []);

  const onFocus = useCallback((event: FocusEvent<HTMLDivElement | HTMLAnchorElement, Element>) => {
    // const from = event.relatedTarget;
    const to = event.target as HTMLAnchorElement;

    const focusedElement = (document.querySelector('#user-nav a:focus') ||
      document.activeElement) as HTMLAnchorElement;

    if (!focusedElement?.dataset?.usernav && !to?.dataset?.usernav) {
      clearTimeout(timeoutRef.current);
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isLoading && user === undefined) {
      const timer = setTimeout(() => setIsLoading(false), 5000);

      return () => clearTimeout(timer);
    } else if (isLoading) {
      setIsLoading(false);
    }
  }, [isLoading, user]);

  if (!user) {
    if (props?.withoutUser && !isLoading) {
      return (
        <a
          href="/signin"
          className="inline-flex w-full flex-row items-center justify-between gap-x-1.5 p-3 font-semibold text-zinc-300 leading-6 transition-[width,color,background-color] duration-500 ease-out hover:bg-zinc-800 active:text-white data-[active=true]:bg-white/10 data-[active=true]:text-white"
        >
          <div className="flex grow animate-pulse flex-row items-center text-left">
            <span className="inline-flex size-6 select-none items-center justify-center rounded-full bg-zinc-700/50" />
            <div className="ml-2 h-4 w-16 max-w-[stretch] rounded-sm bg-zinc-700/50" />
          </div>
        </a>
      );
    }

    return (
      <div className="w-full">
        {!isLoading && user === undefined && (
          <small className="block w-full px-3 text-red-200 text-xs leading-4">
            Loading your account... You can sign in again, or simply try{' '}
            <a href="#refresh" onClick={() => location.reload()}>
              refreshing
            </a>
            .
          </small>
        )}
        <a
          href="/signin"
          className="inline-flex w-full flex-row items-center justify-between gap-x-1.5 p-3 font-semibold text-zinc-300 leading-6 transition-[width,color,background-color] duration-500 ease-out hover:bg-zinc-800 active:text-white data-[active=true]:bg-white/10 data-[active=true]:text-white"
        >
          {isLoading ? 'Loading...' : 'Sign In'}
        </a>
      </div>
    );
  }

  // Here be dragons. We need this menu to work if the JS fails.
  // Makes sure menu can open when there's no JS, and disables weird rules when JS is active
  // Non-JS has "close" button in-menu, but JS closes by clicking on the button again
  return (
    <div
      className="group/userdropdown relative flex w-full grow cursor-pointer flex-col gap-x-1.5 text-left"
      id="user-nav-wrap"
      tabIndex={isReady ? undefined : 0}
      data-open={isOpen}
      onBlur={onBlur}
      onFocus={onFocus}
      autoFocus={false}
    >
      <MenuTrigger>
        <MenuButton
          noIndicator
          variant="plain"
          className={clsx(
            'peer/trigger group/trigger inline-flex w-full flex-row items-center justify-between gap-x-1.5 p-3 font-semibold text-zinc-300 leading-6 transition-[width,color,background-color] duration-500 ease-out hover:bg-zinc-800 active:text-white data-[active=true]:bg-white/10 data-[active=true]:text-white',
            // non-js -- click through so parent gets focused via tabIndex
            '[body:not(.page-is-interactive)_&]:pointer-events-none',
          )}
          onPress={onMenuButtonEvent}
          onKeyUp={onMenuButtonEvent}
          onFocus={onMenuButtonEvent}
          preventFocusOnPress
        >
          <div className="grow text-left">
            <span className="inline-flex size-6 select-none items-center justify-center rounded-full bg-white/40">
              <span className="self-center font-medium text-white text-xs leading-6">
                {user?.firstName?.charAt(0)}
                {user?.lastName?.charAt(0)}
              </span>
            </span>
            <span className="ml-2 max-w-[stretch] whitespace-normal break-all align-middle font-semibold text-sm">
              {user?.firstName || user?.lastName}
            </span>
          </div>
          <ChevronUpIcon
            className={twMerge(
              'size-5 text-zinc-400',
              'hidden',
              'group-focus/trigger:inline-block',
              'group-focus-within/trigger:inline-block',
              // 'group-active/trigger:inline-block',
              'group-has-[a:active]/userdropdown:inline-block',
              'group-focus-within/userdropdown:inline-block',
              'group-focus/userdropdown:inline-block',
            )}
            aria-hidden="true"
          />
          <ChevronDownIcon
            className={twMerge(
              'size-5 text-zinc-400',
              'inline-block',
              'group-focus/trigger:hidden',
              'group-focus-within/trigger:hidden',
              // 'group-active/trigger:hidden',
              'group-has-[a:active]/userdropdown:hidden',
              'group-focus-within/userdropdown:hidden',
              'group-focus/userdropdown:hidden',
            )}
            aria-hidden="true"
          />
        </MenuButton>
        <Popover
          className={twMerge(
            'cursor-auto',
            'text-sm',
            '**:data-[ui=item]:flex',
            'relative w-[calc(100dvw-(--spacing(2))*2)] p-2 md:w-[calc(theme(width.72)-(--spacing(2))*3)]',
            'rounded-sm bg-zinc-800 text-white shadow-md shadow-zinc-700 ring-1 ring-white/20',
            // Default: hidden, pointer-events-none
            'opacity-0 transition-opacity duration-100 ease-out',
            // No-JS: show on focus-within, hover, or active
            '[body:not(.page-is-interactive)_&]:group-focus-within/userdropdown:pointer-events-auto [body:not(.page-is-interactive)_&]:group-focus-within/userdropdown:opacity-100',
            '[body:not(.page-is-interactive)_&]:group-hover/userdropdown:pointer-events-auto [body:not(.page-is-interactive)_&]:group-hover/userdropdown:opacity-100',
            '[body:not(.page-is-interactive)_&]:group-active/userdropdown:pointer-events-auto [body:not(.page-is-interactive)_&]:group-active/userdropdown:opacity-100',
            // With JS: show when open
            isOpen && 'pointer-events-auto opacity-100',
          )}
        >
          <Menu
            id="user-nav"
            autoFocus={false}
            className={twMerge(
              // 'cursor-auto',
              // 'text-sm',
              // '**:data-[ui=item]:flex',
              // // 'opacity-0 transition delay-100 duration-100 ease-out',
              // '[body:not(.page-is-interactive)_&]:invisible',
              // '[body:not(.page-is-interactive)_&]:-translate-y-full',
              // // js - when trigger button has data-focused
              // 'peer-data-[focused=true]/trigger:-translate-y-full peer-data-[focused=true]/trigger:opacity-100',
              // // non-js - when trigger button has :focus
              // 'peer-active/trigger:visible peer-active/trigger:opacity-100',
              // // non-js - when trigger button is active
              // 'peer-active/trigger:[body:not(.page-is-interactive)_&]:visible peer-active/trigger:[body:not(.page-is-interactive)_&]:opacity-100',
              // // non-js - when link inside dropdown is active
              // '[body:not(.page-is-interactive)_&]:group-has-[a:active]/userdropdown:visible [body:not(.page-is-interactive)_&]:group-has-[a:active]/userdropdown:opacity-100',
              // // non-js - when link inside is hovered
              // 'has-[a:hover]:[body:not(.page-is-interactive)_&]:visible has-[a:hover]:[body:not(.page-is-interactive)_&]:opacity-100',
              // // non-js - when link inside dropdown has focus
              // 'group-focus-within/userdropdown:has-[a[href]:focus]:[body:not(.page-is-interactive)_&]:visible group-focus-within/userdropdown:has-[a[href]:focus]:[body:not(.page-is-interactive)_&]:opacity-100',
              // // non-js - when whole area is clicked
              // 'group-focus/userdropdown:[body:not(.page-is-interactive)_&]:visible group-focus/userdropdown:[body:not(.page-is-interactive)_&]:opacity-100',
              // // js - when link inside dropdown has data-focused
              // 'group-has-[a[data-focused="true"]]/userdropdown:[body.page-is-interactive_&]:-translate-y-full group-has-[a[data-focused="true"]]/userdropdown:[body.page-is-interactive_&]:opacity-100',
              // // js -
              // '[body.page-is-interactive_&]:pointer-events-none [body.page-is-interactive_&]:group-data-[open=true]/userdropdown:pointer-events-auto',
            )}
            onAction={() => typeof document !== 'undefined' && document?.body?.blur()}
            onClose={onClose}
          >
            {LINKS.map((link, idx) => {
              const IconComponent = link.icon;

              if (isReady && link.className?.includes('[body.page-is-interactive_&]:hidden')) {
                return null;
              }

              return (
                <Fragment key={link.href || idx}>
                  {link.divider ? (
                    <MenuSeparator
                      key={link.href || idx}
                      className={clsx('my-1 h-px w-full border-0 bg-white/10', link.className)}
                    />
                  ) : (
                    <MenuItem
                      key={link.href || idx}
                      destructive={link.destructive}
                      href={link.href}
                      data-usernav={true}
                      className={clsx(
                        'cursor-pointer',
                        'inline-flex w-full items-center space-x-2 px-4 py-2',
                        'text-sm text-zinc-300 hover:text-white',
                        'focus:bg-white/5 focus:text-white',
                        link.className,
                        link.destructive && 'text-red-400 focus:bg-red-400/10 focus:text-red-400',
                      )}
                    >
                      {IconComponent && (
                        <AccessibleIcon>
                          <IconComponent
                            height={16}
                            className={
                              (clsx('size-4 shrink-0 fill-inherit'),
                              link.destructive && 'fill-red-400')
                            }
                            aria-hidden="true"
                          />
                        </AccessibleIcon>
                      )}
                      <MenuItemLabel tabIndex={0}>{link.text}</MenuItemLabel>
                    </MenuItem>
                  )}
                </Fragment>
              );
            })}
          </Menu>
        </Popover>
      </MenuTrigger>
    </div>
  );
}
